// src/admin/pages/UserProgressSummary.jsx
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  CheckCircle2,
  Clock3,
  Loader2,
  RotateCcw,
  ShieldCheck,
  TimerReset,
} from "lucide-react";
import { adminService } from "../../services/adminService";

const UserProgressSummary = () => {
  const { userId } = useParams();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const loadSummary = async () => {
      setLoading(true);
      setError("");

      try {
        const data = await adminService.getUserProgressSummary(userId);
        setSummary(data);
      } catch (err) {
        console.error(err);
        setError("Failed to fetch progress summary.");
      } finally {
        setLoading(false);
      }
    };

    loadSummary();
  }, [userId]);

  const payload = summary || {};
  const user = payload.user || {};
  const auth = payload.auth || {};
  const progressOverview = payload.progress_overview || {};
  const currentState = payload.current_state || null;
  const courses = Array.isArray(payload.courses) ? payload.courses : [];

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="animate-spin text-[#54B0AF]" size={32} />
      </div>
    );
  }

  if (error) return <div className="text-title text-red-600">{error}</div>;

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-display">Progress Summary</h1>
          <p className="text-sm text-muted">
            {formatUserName(user)} {user.email ? `(${user.email})` : ""}
          </p>
          <p className="text-xs text-muted">User ID: {userId}</p>
        </div>

        <Link
          to="/admin/users"
          className="rounded-md border px-4 py-2 text-sm transition hover:bg-gray-50"
        >
          Back to Users
        </Link>
      </div>

      <div className="stakeholder-summary-card space-y-6 rounded-lg bg-white p-6 shadow">
        <div className="grid gap-4 md:grid-cols-3">
          <InfoCard
            label="User"
            value={formatUserName(user)}
            helper={user.email || "--"}
          />
          <InfoCard
            label="Role"
            value={user.role_name || "--"}
            helper={`Has logged in: ${formatBool(user.has_logged_in)}`}
          />
          <InfoCard
            label="Last login"
            value={formatDateTime(user.last_login_at || auth.last_login_at)}
            helper={`Created: ${formatDate(user.created_at)}`}
          />
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <SummaryPanel
            title="Authentication"
            icon={<ShieldCheck size={16} />}
            items={[
              {
                label: "Login count",
                value: formatNumber(auth.login_count),
              },
              {
                label: "First login",
                value: formatDateTime(auth.first_login_at),
              },
              {
                label: "Last login",
                value: formatDateTime(auth.last_login_at),
              },
              {
                label: "Methods",
                value: formatLoginMethods(auth.login_methods),
              },
            ]}
          />

          <SummaryPanel
            title="Progress overview"
            icon={<TimerReset size={16} />}
            items={[
              {
                label: "First progress",
                value: formatDateTime(progressOverview.first_progress_at),
              },
              {
                label: "Last progress",
                value: formatDateTime(progressOverview.last_progress_at),
              },
              {
                label: "Last completed",
                value: formatDateTime(progressOverview.last_completed_at),
              },
              {
                label: "Courses",
                value: formatFraction(
                  progressOverview.completed_modules,
                  progressOverview.total_modules,
                  "modules completed"
                ),
              },
            ]}
          />
        </div>

        <div className="grid gap-3 md:grid-cols-5">
          <StatPill
            label="Subsections"
            value={formatFraction(
              progressOverview.completed_subsections,
              progressOverview.total_subsections
            )}
          />
          <StatPill
            label="Attempted"
            value={formatNumber(progressOverview.attempted_subsections)}
          />
          <StatPill
            label="In progress"
            value={formatNumber(progressOverview.in_progress_subsections)}
          />
          <StatPill
            label="Pending"
            value={formatNumber(progressOverview.pending_subsections)}
          />
          <StatPill
            label="Revisited"
            value={formatNumber(progressOverview.revisited_subsections)}
          />
        </div>

        <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
          <div className="rounded-md border border-gray-100 bg-[#F5F9F9] p-4">
            <div className="mb-2 text-sm font-semibold text-title">
              Current state
            </div>

            {currentState ? (
              <div className="grid gap-3 text-sm md:grid-cols-2">
                <div>
                  <span className="font-medium">Module:</span>{" "}
                  {formatTitleWithId(
                    currentState.current_module_title,
                    currentState.current_module_id
                  )}
                </div>
                <div>
                  <span className="font-medium">Subsection:</span>{" "}
                  {formatTitleWithId(
                    currentState.current_subsection_title,
                    currentState.current_subsection_id
                  )}
                </div>
                <div>
                  <span className="font-medium">Course:</span>{" "}
                  {formatTitleWithId(
                    findCourseTitle(courses, currentState.course_id),
                    currentState.course_id
                  )}
                </div>
              </div>
            ) : (
              <div className="text-sm text-muted">
                No active module or subsection.
              </div>
            )}
          </div>

          <div className="rounded-md border border-gray-100 bg-[#FCFDFD] p-4">
            <div className="mb-3 text-sm font-semibold text-title">
              Timing insights
            </div>
            <div className="grid gap-3 text-sm md:grid-cols-2">
              <MetricLine
                label="Average completion"
                value={formatMinutes(
                  progressOverview.avg_subsection_completion_time_minutes
                )}
              />
              <MetricLine
                label="Median completion"
                value={formatMinutes(
                  progressOverview.median_subsection_completion_time_minutes
                )}
              />
              <MetricLine
                label="Total courses"
                value={formatNumber(progressOverview.total_courses)}
              />
              <MetricLine
                label="Completed modules"
                value={formatNumber(progressOverview.completed_modules)}
              />
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <h2 className="text-title font-semibold">Course progress</h2>

          {courses.length === 0 ? (
            <div className="text-sm text-muted">No course progress available.</div>
          ) : (
            <div className="grid gap-4">
              {courses.map((course, index) => {
                const courseName =
                  course.course_title || course.title || `Course ${index + 1}`;
                const totals = course.totals || {};
                const totalSubsections =
                  totals.total_subsections ?? totals.totalSubsections ?? 0;
                const completedSubsections =
                  totals.completed_subsections ??
                  totals.completedSubsections ??
                  0;
                const inProgressSubsections =
                  totals.in_progress_subsections ??
                  totals.inProgressSubsections ??
                  0;
                const pendingSubsections =
                  totals.pending_subsections ??
                  totals.pendingSubsections ??
                  0;
                const totalModules =
                  totals.total_modules ?? totals.totalModules ?? 0;
                const completedModules =
                  totals.completed_modules ?? totals.completedModules ?? 0;
                const completionPercent = totalSubsections
                  ? Math.round((completedSubsections / totalSubsections) * 100)
                  : null;
                const timelineEntries = Array.isArray(course.completion_timeline)
                  ? [...course.completion_timeline].reverse()
                  : [];
                const moduleSummaries = Array.isArray(course.module_summaries)
                  ? course.module_summaries
                  : [];
                const subsectionProgress = Array.isArray(
                  course.subsection_progress
                )
                  ? course.subsection_progress
                  : [];
                const activeItems = subsectionProgress.filter(
                  (item) => item.status === "in_progress"
                );

                return (
                  <div
                    key={course.course_id || index}
                    className="rounded-xl border border-gray-100 p-4 shadow-sm md:p-5"
                  >
                    <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                      <div>
                        <div className="text-title font-semibold">
                          {courseName}
                        </div>
                        <div className="text-xs text-muted">
                          Status: {course.enrollment_status || "--"} | Enrolled:{" "}
                          {formatDate(course.enrolled_at)} | Started:{" "}
                          {formatDateTime(course.first_started_at)}
                        </div>
                      </div>
                      <div className="text-sm text-muted">
                        Last progress: {formatDateTime(course.last_progress_at)}
                      </div>
                    </div>

                    <div className="mt-4 grid gap-3 text-sm md:grid-cols-4">
                      <StatPill
                        label="Completion"
                        value={
                          completionPercent !== null
                            ? `${completionPercent}%`
                            : "--"
                        }
                      />
                      <StatPill
                        label="Subsections"
                        value={`${completedSubsections}/${totalSubsections}`}
                      />
                      <StatPill
                        label="Modules"
                        value={`${completedModules}/${totalModules}`}
                      />
                      <StatPill
                        label="In progress"
                        value={String(inProgressSubsections)}
                      />
                    </div>

                    <div className="mt-3 grid gap-3 text-xs text-muted md:grid-cols-2 xl:grid-cols-4">
                      <MetaPill
                        label="Attempted"
                        value={formatNumber(course.attempted_subsections)}
                      />
                      <MetaPill
                        label="Revisited"
                        value={formatNumber(course.revisited_subsections)}
                      />
                      <MetaPill
                        label="Avg completion"
                        value={formatMinutes(
                          course.avg_subsection_completion_time_minutes
                        )}
                      />
                      <MetaPill
                        label="Median completion"
                        value={formatMinutes(
                          course.median_subsection_completion_time_minutes
                        )}
                      />
                    </div>

                    <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-muted">
                      <span>Pending subsections: {pendingSubsections}</span>
                      {course.active_module?.title && (
                        <span className="rounded-full bg-[#F5F9F9] px-2.5 py-1 text-title">
                          Active module: {course.active_module.title}
                        </span>
                      )}
                      {course.last_completed_at && (
                        <span className="rounded-full bg-[#F5F9F9] px-2.5 py-1 text-title">
                          Last completed: {formatDateTime(course.last_completed_at)}
                        </span>
                      )}
                    </div>

                    {activeItems.length > 0 && (
                      <div className="mt-4 rounded-lg bg-[#FAFCFC] p-4">
                        <div className="mb-2 text-sm font-semibold text-title">
                          Active subsections
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {activeItems.map((item) => (
                            <Tag key={item.subsection_id} tone="default">
                              {item.subsection_title || "--"}
                            </Tag>
                          ))}
                        </div>
                      </div>
                    )}

                    {moduleSummaries.length > 0 && (
                      <details className="mt-4 rounded-lg border border-gray-100 bg-[#FCFDFD] p-4">
                        <summary className="cursor-pointer text-sm font-semibold text-[#54B0AF]">
                          Module breakdown ({moduleSummaries.length})
                        </summary>
                        <div className="mt-3 grid gap-3">
                          {moduleSummaries.map((module) => (
                            <div
                              key={module.module_id}
                              className="rounded-lg border border-gray-100 bg-white p-3"
                            >
                              <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                                <div className="text-sm font-semibold text-title">
                                  {module.module_order
                                    ? `Module ${module.module_order}: `
                                    : ""}
                                  {module.module_title || "--"}
                                </div>
                                <div className="flex flex-wrap gap-2">
                                  <Tag tone={module.is_completed ? "accent" : "default"}>
                                    {module.is_completed ? "Completed" : "In progress"}
                                  </Tag>
                                  {!module.is_unlocked && (
                                    <Tag tone="warning">Locked</Tag>
                                  )}
                                </div>
                              </div>

                              <div className="mt-3 grid gap-3 text-xs text-muted md:grid-cols-4">
                                <MetaPill
                                  label="Subsections"
                                  value={formatFraction(
                                    module.totals?.completed_subsections,
                                    module.totals?.total_subsections
                                  )}
                                />
                                <MetaPill
                                  label="In progress"
                                  value={formatNumber(
                                    module.totals?.in_progress_subsections
                                  )}
                                />
                                <MetaPill
                                  label="Revisited"
                                  value={formatNumber(
                                    module.revisited_subsections
                                  )}
                                />
                                <MetaPill
                                  label="Avg completion"
                                  value={formatMinutes(
                                    module.avg_subsection_completion_time_minutes
                                  )}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      </details>
                    )}

                    <div className="mt-5 border-t border-gray-100 pt-4">
                      <div className="flex items-center justify-between gap-3">
                        <h3 className="text-sm font-semibold text-title">
                          Completion timeline
                        </h3>
                        <div className="text-xs text-muted">Newest first</div>
                      </div>

                      {timelineEntries.length === 0 ? (
                        <div className="mt-3 rounded-lg border border-dashed border-gray-200 bg-[#FAFCFC] px-4 py-6 text-sm text-muted">
                          No completed subsections yet.
                        </div>
                      ) : (
                        <div className="mt-4 space-y-3">
                          {timelineEntries.map((item, itemIndex) => (
                            <TimelineItem
                              key={
                                item.subsection_id ||
                                item.completed_at ||
                                `${course.course_id || index}-${itemIndex}`
                              }
                              item={item}
                              isLast={itemIndex === timelineEntries.length - 1}
                              isLatest={itemIndex === 0}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserProgressSummary;

const SummaryPanel = ({ title, icon, items }) => (
  <div className="rounded-md border border-gray-100 bg-[#FCFDFD] p-4">
    <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-title">
      <span className="text-[#54B0AF]">{icon}</span>
      {title}
    </div>
    <div className="grid gap-3 md:grid-cols-2">
      {items.map((item) => (
        <MetricLine key={item.label} label={item.label} value={item.value} />
      ))}
    </div>
  </div>
);

const InfoCard = ({ label, value, helper }) => (
  <div className="rounded-md bg-[#F5F9F9] p-4">
    <div className="text-xs uppercase tracking-wide text-muted">{label}</div>
    <div className="mt-1 text-title font-semibold">{value}</div>
    {helper && <div className="mt-1 text-xs text-muted">{helper}</div>}
  </div>
);

const MetricLine = ({ label, value }) => (
  <div className="rounded-md bg-[#F5F9F9] px-3 py-2">
    <div className="text-xs uppercase tracking-wide text-muted">{label}</div>
    <div className="mt-1 text-sm font-semibold text-title">{value}</div>
  </div>
);

const MetaPill = ({ label, value }) => (
  <div className="rounded-md bg-[#F5F9F9] px-3 py-2">
    <div className="text-[11px] uppercase tracking-wide text-muted">
      {label}
    </div>
    <div className="mt-1 text-sm font-medium text-title">{value}</div>
  </div>
);

const TimelineItem = ({ item, isLatest, isLast }) => {
  const completionCount = item.completion_count ?? 0;
  const wasRevisited = completionCount > 1;

  return (
    <div className="flex gap-3 rounded-xl border border-gray-100 bg-[#FCFDFD] p-4">
      <div className="flex flex-col items-center">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#E8F5F4] text-[#2F8E8D]">
          <CheckCircle2 size={18} />
        </div>
        {!isLast && <div className="mt-2 h-full w-px bg-gray-200" />}
      </div>

      <div className="min-w-0 flex-1 space-y-2">
        <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <div className="text-sm font-semibold text-title">
                {item.subsection_title || "--"}
              </div>
              {item.type && <Tag>{formatLabel(item.type)}</Tag>}
              {isLatest && <Tag tone="accent">Latest completion</Tag>}
              {wasRevisited && (
                <Tag tone="warning">
                  <span className="inline-flex items-center gap-1">
                    <RotateCcw size={12} />
                    Revisited {completionCount}x
                  </span>
                </Tag>
              )}
            </div>

            <div className="mt-1 text-xs text-muted">
              {buildTimelinePath(item)}
            </div>
          </div>

          <div className="flex items-center gap-1 whitespace-nowrap text-xs text-muted">
            <Clock3 size={12} />
            {formatDateTime(item.completed_at)}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 text-xs text-muted">
          {item.first_completed_at &&
            item.first_completed_at !== item.completed_at && (
              <span className="rounded-full bg-[#F5F9F9] px-2.5 py-1">
                First completed: {formatDateTime(item.first_completed_at)}
              </span>
            )}
          {wasRevisited && item.last_completed_at && (
            <span className="rounded-full bg-[#F5F9F9] px-2.5 py-1">
              Last completed: {formatDateTime(item.last_completed_at)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

const Tag = ({ children, tone = "default" }) => {
  const toneClassName =
    tone === "accent"
      ? "bg-[#E8F5F4] text-[#2F8E8D]"
      : tone === "warning"
      ? "bg-[#FFF4E5] text-[#9A6700]"
      : "bg-[#EEF2F7] text-[#516074]";

  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-medium ${toneClassName}`}
    >
      {children}
    </span>
  );
};

const formatBool = (value) => {
  if (value === true) return "Yes";
  if (value === false) return "No";
  return "--";
};

const formatDateTime = (value) => {
  if (!value) return "--";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--";
  return date.toLocaleString();
};

const formatUserName = (user) => {
  const name = `${user.first_name || ""} ${user.last_name || ""}`.trim();
  return name || user.email || "--";
};

const formatDate = (value) => {
  if (!value) return "--";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--";
  return date.toLocaleDateString();
};

const formatTitleWithId = (title, id) => {
  if (title) {
    return (
      <>
        {title}
        {id ? <span className="ml-2 text-xs text-muted">({id})</span> : null}
      </>
    );
  }
  return id || "--";
};

const formatFraction = (current, total, suffix = "") => {
  const left = formatNumber(current);
  const right = formatNumber(total);
  return suffix ? `${left}/${right} ${suffix}` : `${left}/${right}`;
};

const formatNumber = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "--";
  }
  return Number(value).toLocaleString();
};

const formatMinutes = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "--";
  }

  const rounded = Math.round(Number(value));
  if (rounded < 60) return `${rounded} min`;

  const hours = Math.floor(rounded / 60);
  const minutes = rounded % 60;
  return minutes ? `${hours}h ${minutes}m` : `${hours}h`;
};

const formatLoginMethods = (value) => {
  if (!value || typeof value !== "object") return "--";
  const entries = Object.entries(value);
  if (entries.length === 0) return "--";
  return entries
    .map(([method, count]) => `${formatLabel(method)} (${formatNumber(count)})`)
    .join(", ");
};

const findCourseTitle = (courses, courseId) => {
  if (!courseId) return "";
  const match = courses.find((course) => course.course_id === courseId);
  return match?.course_title || match?.title || "";
};

const buildTimelinePath = (item) => {
  const parts = [
    item.module_title &&
      `Module ${item.module_order ? `${item.module_order}: ` : ""}${item.module_title}`,
    item.section,
    item.subsection_order ? `Step ${item.subsection_order}` : "",
  ].filter(Boolean);

  return parts.join(" | ") || "--";
};

const formatLabel = (value) => {
  if (!value) return "--";
  return String(value)
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const StatPill = ({ label, value }) => (
  <div className="rounded-md bg-[#F5F9F9] px-3 py-2">
    <div className="text-xs uppercase tracking-wide text-muted">{label}</div>
    <div className="mt-1 text-title font-semibold">{value}</div>
  </div>
);
